import TabErrorBanner from "@/components/v2/molecules/admin/shared/TabErrorBanner";
import MultiPropertyCalendar from "@/components/v2/organisms/admin/tabs/AdminCalendarTab/MultiPropertyCalendar";
import CalendarFiltersBar from "@/components/v2/molecules/admin/tabs/AdminCalendarTab/CalendarFiltersBar";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import useFilteredProperties from "@/hooks/useFilteredProperties";
import { useMemo, useState, useRef } from "react";
import { filterActiveContracts } from "@/utils/contractUtils";
import { addMonths, startOfMonth, format } from "date-fns";
import { es } from "date-fns/locale";
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

type ScrollFunctions = {
  scrollToPreviousWeek: () => void;
  scrollToNextWeek: () => void;
  scrollToToday: () => void;
  scrollToDay: (date: Date) => void;
};

const AdminCalendarTab = () => {
  const {
    data: properties = [],
    isLoading: isLoadingProperties,
    error: errorProperties,
    refetch: refetchProperties,
  } = useProperties();

  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  const { filters, handleFilterChange, filteredProperties } =
    useFilteredProperties({ properties, contracts });

  const hasScrolledToTodayRef = useRef(true);
  const scrollFunctionsRef = useRef<ScrollFunctions | null>(null);

  // Cascading: filter properties for the dropdown based on selected organizations
  const propertiesForCascade = useMemo(() => {
    // Start with properties not hidden from calendar
    let result = properties.filter(p => !p.hiddenFromCalendar);
    
    // Always filter to properties with active contracts
    const activeContracts = filterActiveContracts(contracts);
    const activePropertyIds = new Set(activeContracts.map(c => c.property_id));
    result = result.filter(p => activePropertyIds.has(p.id));
    
    // If organizations are selected, further filter by org
    if (filters.organizationIds.length > 0) {
      const propertyIdsInOrgs = new Set(
        activeContracts
          .filter(c => c.organization_id && filters.organizationIds.includes(c.organization_id))
          .map(c => c.property_id)
      );
      result = result.filter(p => propertyIdsInOrgs.has(p.id));
    }
    
    // Sort by sortOrder (nulls/999 last)
    result.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
    
    return result;
  }, [properties, contracts, filters.organizationIds]);

  // Generate month options: 6 months back + current + 12 months forward
  const monthOptions = useMemo(() => {
    const now = new Date();
    const options = [];
    for (let i = -6; i <= 12; i++) {
      const date = addMonths(now, i);
      options.push({
        label: format(date, "MMMM yyyy", { locale: es }),
        value: i,
        date: startOfMonth(date),
      });
    }
    return options;
  }, []);

  const [visibleMonthIndex, setVisibleMonthIndex] = useState(0);
  const [visibleMonth, setVisibleMonth] = useState<string>("");

  const handleMonthSelect = (value: string) => {
    const option = monthOptions.find(o => String(o.value) === value);
    if (option && scrollFunctionsRef.current) {
      scrollFunctionsRef.current.scrollToDay(option.date);
      setVisibleMonth(option.label);
      hasScrolledToTodayRef.current = false;
    }
  };

  const handlePrevious = () => {
    scrollFunctionsRef.current?.scrollToPreviousWeek();
    hasScrolledToTodayRef.current = false;
  };

  const handleNext = () => {
    scrollFunctionsRef.current?.scrollToNextWeek();
    hasScrolledToTodayRef.current = false;
  };

  const handleToday = () => {
    scrollFunctionsRef.current?.scrollToToday();
  };

  if (isLoadingProperties) {
    return (
      <div className="flex justify-center py-8">
        <div className="text-sm text-gray-500">Cargando propiedades...</div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">
          No hay propiedades disponibles para mostrar en el calendario.
        </p>
      </div>
    );
  }

  if (errorProperties) {
    return (
      <TabErrorBanner
        title="Error al cargar propiedades"
        message="No se pudieron cargar las propiedades. Por favor, intenta nuevamente."
        onRetry={() => refetchProperties()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <AdminPageHeader title="Calendario" />
        <CalendarFiltersBar
          organizations={organizations}
          properties={propertiesForCascade}
          selectedOrganizations={filters.organizationIds}
          selectedProperties={filters.propertyIds}
          onOrganizationsChange={(ids) => handleFilterChange('organizationIds', ids)}
          onPropertiesChange={(ids) => handleFilterChange('propertyIds', ids)}
          visibleMonth={visibleMonth}
          monthOptions={monthOptions.map(opt => ({ value: String(opt.value), label: opt.label }))}
          onMonthSelect={handleMonthSelect}
          onPrevious={handlePrevious}
          onToday={handleToday}
          onNext={handleNext}
        />
      </div>
      <MultiPropertyCalendar 
        properties={filteredProperties}
        visibleMonthIndex={visibleMonthIndex}
        setVisibleMonthIndex={setVisibleMonthIndex}
        setVisibleMonth={setVisibleMonth}
        hasScrolledToTodayRef={hasScrolledToTodayRef}
        onScrollFunctionsReady={(fns) => {
          scrollFunctionsRef.current = fns;
        }}
      />
    </div>
  );
};

export default AdminCalendarTab;
